import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
  memo,
  FC,
} from "react";
import { useDropzone, FileRejection } from "react-dropzone";
import { Button } from "semantic-ui-react";
import { v4 as uuidv4 } from "uuid";
import { GridContextProvider, GridDropZone, GridItem } from "react-grid-dnd";

import {
  DROPZONE_MAX_FILE_3MB,
  DROPZONE_MAX_FILE_3MB_TEXT,
} from "../../../../../../App/configs/DropzoneConfig";
import { uploadImageService } from "../../../../../../App/services/AppServices";
import ButtonCustom from "../../../../../../../components/Button";
import SizeBox from "../../../../../../../components/SizeBox";
import { toastError } from "../../../../../../../components/Toast";
import { LocalizationContext } from "../../../../../../../locales/Translation";
import { FormikErrors, FormikTouched } from "formik";

// Types
interface PhotoItem {
  id: string;
  file_key: string;
  uri: string;
  is_primary?: boolean;
}

interface ValidatedDropzoneProps {
  value: PhotoItem[];
  setPhotos: (photos: PhotoItem[]) => void;
  deletePhotos: (id: string) => void;
  isDragDropChangeOrder?: boolean;
  onDragEnd: (
    sourceId: string,
    sourceIndex: number,
    targetIndex: number,
    targetId?: string | undefined
  ) => void;
  containerClass?: string;
  boxClass?: string;
  touched?: boolean | FormikTouched<any>[] | undefined;
  error?: string | string[] | FormikErrors<any>[] | undefined;
  maxImages?: number;
  [x: string]: unknown;
}

// Constants
const DROPZONE_CONFIG = {
  accept: [".png", ".jpg", ".jpeg", ".gif"],
  maxSize: DROPZONE_MAX_FILE_3MB,
  useFsAccessApi: false,
  multiple: true,
};

const GRID_CONFIG = {
  boxesPerRow: 4,
  rowHeight: 156,
};

const DropzoneComponentValidated: FC<ValidatedDropzoneProps> = ({
  value,
  setPhotos,
  deletePhotos,
  isDragDropChangeOrder = false,
  onDragEnd,
  containerClass = "",
  boxClass = "",
  touched = false,
  error = "",
  maxImages = 10,
}) => {
  const { translations } = useContext(LocalizationContext);
  const [isDragOver, setIsDragOver] = useState(false);

  // Memoized accept configuration
  const acceptConfig = useMemo(() => DROPZONE_CONFIG.accept, []);

  // Upload handler with proper error handling
  const handleFileUpload = useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) return;

      try {
        // Create upload promises
        const uploadPromises = acceptedFiles.map((file) => {
          const formData = new FormData();
          formData.append("avatar", file);
          return uploadImageService(formData);
        });

        const responses = await Promise.all(uploadPromises);

        // Transform responses to photo items
        const newPhotos: PhotoItem[] = responses.map((response) => ({
          id: response?.data?.id || uuidv4(),
          file_key: response?.data?.avatar_uri,
          uri: response?.data?.uri,
        }));

        setPhotos([...value, ...newPhotos]);
      } catch (error) {
        console.error("Upload failed:", error);
        toastError("Failed to upload images. Please try again.");
      } finally {
        setIsDragOver(false);
      }
    },
    [value, setPhotos]
  );

  // File rejection handler
  const handleFileRejection = useCallback((fileRejections: FileRejection[]) => {
    fileRejections.forEach(({ file, errors }) => {
      const hasFileSizeError = errors.some(
        (error) => error.code === "file-too-large"
      );

      if (hasFileSizeError) {
        toastError(
          `File ${file.name} is larger than ${DROPZONE_MAX_FILE_3MB_TEXT}`
        );
      }
    });
  }, []);

  // Dropzone configuration
  const dropzoneConfig = useMemo(
    () => ({
      ...DROPZONE_CONFIG,
      accept: acceptConfig,
      onDrop: handleFileUpload,
      onDropRejected: handleFileRejection,
      onDragOver: () => setIsDragOver(true),
      onDragLeave: () => setIsDragOver(false),
      maxSize: DROPZONE_MAX_FILE_3MB,
      maxFiles: maxImages,
    }),
    [acceptConfig, handleFileUpload, handleFileRejection]
  );

  const { getRootProps, getInputProps } = useDropzone(dropzoneConfig);

  // Photo item component
  const PhotoItem: FC<{
    photo: PhotoItem;
    className?: string;
  }> = memo(
    ({
      photo,
      className = "dropzone-preview__thumb-wrapper",
    }: {
      photo: PhotoItem;
      className?: string;
    }) => (
      <div className={className} key={photo.id}>
        <div
          className={`dropzone-preview__thumb-box${
            photo.is_primary ? " active" : ""
          }`}
        >
          <ButtonCustom
            type="button"
            onClick={() => deletePhotos(photo.id)}
            className="dropzone-preview__delete-cta"
            aria-label={`Delete image ${photo.id}`}
          >
            <i className="uil uil-trash" />
          </ButtonCustom>

          <Button
            className={`dropzone-preview__set-primary-cta${
              photo.is_primary ? " active" : ""
            }`}
            aria-label={`Set as primary image`}
          >
            <i className="uil uil-check" />
          </Button>

          <img
            className="dropzone-preview__thumb"
            src={photo.uri}
            alt="Uploaded image"
            loading="lazy"
          />
        </div>
      </div>
    )
  );

  PhotoItem.displayName = "PhotoItem";

  // Grid height calculation
  const gridHeight = useMemo(() => {
    const rows = Math.ceil(value.length / GRID_CONFIG.boxesPerRow);
    return GRID_CONFIG.rowHeight * rows;
  }, [value.length]);

  const renderDragDropGrid = () => (
    <GridContextProvider onChange={onDragEnd}>
      <GridDropZone
        id="photo-grid"
        boxesPerRow={GRID_CONFIG.boxesPerRow}
        rowHeight={GRID_CONFIG.rowHeight}
        style={{ height: `${gridHeight}px` }}
      >
        {value.map((photo: PhotoItem, index: number) => (
          <GridItem
            key={`grid_item_idx_${index}`}
            className="react-dnd-grid-item"
          >
            <PhotoItem
              photo={photo}
              className="dropzone-preview__thumb-wrapper"
            />
          </GridItem>
        ))}
      </GridDropZone>
    </GridContextProvider>
  );

  // Dropzone class names
  const dropzoneClasses = [
    "dropzone",
    "dropzone-box",
    "mb-0",
    isDragOver && "drag-over",
    boxClass,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section className={`mt-3 ${containerClass}`}>
      {/* Photo thumbnails */}
      {isDragDropChangeOrder ? (
        renderDragDropGrid()
      ) : (
        <>
          <SizeBox height="24px" />
          <aside className="dropzone-thumbnails">
            {value.filter(Boolean).map((photo, index) => (
              <PhotoItem key={`photo_item_idx_${index}`} photo={photo} />
            ))}
          </aside>
          <SizeBox height="24px" />
        </>
      )}

      {/* Dropzone area */}
      <div {...getRootProps({ className: dropzoneClasses })}>
        <input {...getInputProps()} />

        <div className="dropzone-guide-title">
          {translations.dropYourImageFilesHereOr}
        </div>

        <div className="dropzone-guide-subtitle">
          JPG, GIF & PNG. {translations.maxFileSizeOf} 3MB
        </div>

        <div className="dropzone-guide-subtitle mb-0">
          {translations.maximum10Images}
        </div>
      </div>

      {/* Error message */}
      {touched && error && (
        <span className="error" role="alert">
          {error as string}
        </span>
      )}
    </section>
  );
};

export default DropzoneComponentValidated;
